import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, Check, Copy, KeyRound, Loader2, Plug, Plus, ShieldCheck } from "lucide-react";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatDateTime } from "../lib/assist";
import { STATE_LABELS, STATE_TONES, claudeCodeCommand, connectionSnippet, createErrors, domainLabel, domainsText, expiryText, mcpUrl } from "../lib/agents";
import { LoadError, SectionHeader } from "./Settings";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

const TONES = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  bad: "bg-red-50 text-red-700 border-red-200",
  muted: "bg-slate-100 text-slate-600 border-slate-200",
};

function StatePill({ state, testid }) {
  return <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap ${TONES[STATE_TONES[state]]}`}>{STATE_LABELS[state]}</span>;
}

function TokenRow({ token, showOwner, onRevoke }) {
  const connection = token.kind === "connection";
  return (
    <li className="px-4 py-4 sm:px-5 flex flex-col gap-3 sm:flex-row sm:items-start" data-testid={`${connection ? "connection" : "token"}-${token.id}`}>
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-semibold text-sm text-slate-900">{connection ? token.client_name : token.label}</h4>
          <StatePill state={token.state} testid={`${connection ? "connection" : "token"}-state-${token.id}`} />
          {token.prefix && <code className="text-xs text-slate-500 bg-slate-100 rounded px-1.5 py-0.5">{token.prefix}…</code>}
        </div>
        {showOwner && <p className="text-xs text-slate-500">Compte : {token.owner_name}{token.mine ? " (vous)" : ""}</p>}
        <p className="text-sm text-slate-600">{domainsText(token.domains)}</p>
        <p className="text-xs text-slate-500">
          {expiryText(token)} · {token.last_used_at ? `dernier appel ${formatDateTime(token.last_used_at)}` : "jamais utilisé"} · {token.calls_7d} appel{token.calls_7d > 1 ? "s" : ""} sur 7 jours
        </p>
      </div>
      {token.state === "active" && (
        <Button variant="outline" size="sm" className="shrink-0 text-red-700 border-red-200 hover:bg-red-50 w-full sm:w-auto min-h-[44px] sm:min-h-0" onClick={() => onRevoke(token)} data-testid={`btn-revoke-${token.id}`}>
          Révoquer
        </Button>
      )}
    </li>
  );
}

function CreateDialog({ open, onOpenChange, overview, onCreated }) {
  const [form, setForm] = useState({ label: "", days: "30", domains: [] });
  const [showDomains, setShowDomains] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);
  useEffect(() => { if (open) { setForm({ label: "", days: "30", domains: [] }); setShowDomains(false); setTouched(false); } }, [open]);
  const errors = createErrors(form);
  const toggle = (domain) => setForm((f) => ({ ...f, domains: f.domains.includes(domain) ? f.domains.filter((d) => d !== domain) : [...f.domains, domain] }));
  const submit = async (event) => {
    event.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length) return;
    setSaving(true);
    try {
      const { data } = await api.post("/agents/tokens", { label: form.label.trim(), days: Number(form.days), domains: form.domains });
      onOpenChange(false);
      onCreated(data);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(next) => !saving && onOpenChange(next)}>
      <DialogContent data-testid="create-token-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau jeton d'accès</DialogTitle>
          <DialogDescription>L'agent agira comme votre compte, en lecture seule. Le jeton ne s'affichera qu'une fois.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" id="create-token-form">
          <div className="space-y-1">
            <label htmlFor="token-label" className="text-sm font-medium text-slate-700">Nom (pour vous y retrouver)</label>
            <Input id="token-label" value={form.label} maxLength={60} placeholder="Ex. Claude — suivi commercial" onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              aria-invalid={touched && !!errors.label} data-testid="token-label" />
            {touched && errors.label && <p className="text-xs text-red-600">{errors.label}</p>}
          </div>
          <div className="space-y-1">
            <label htmlFor="token-days" className="text-sm font-medium text-slate-700">Durée de validité</label>
            <div className="flex items-center gap-2">
              <Input id="token-days" type="number" min={1} max={overview.max_days} className="w-24" value={form.days} onChange={(e) => setForm((f) => ({ ...f, days: e.target.value }))}
                aria-invalid={touched && !!errors.days} data-testid="token-days" />
              <span className="text-sm text-slate-500">jours (au plus {overview.max_days})</span>
            </div>
            {touched && errors.days && <p className="text-xs text-red-600">{errors.days}</p>}
          </div>
          <div className="space-y-2">
            <button type="button" className="text-sm font-medium text-jamora hover:underline min-h-[44px] sm:min-h-0" onClick={() => setShowDomains((v) => !v)} data-testid="toggle-domains">
              {showDomains ? "Masquer la limitation par domaine" : "Limiter à certains domaines (facultatif)"}
            </button>
            {showDomains && (
              <fieldset className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="domains-list">
                <legend className="text-xs text-slate-500 mb-1">Sans choix, l'agent peut lire tout ce que votre compte peut lire, sauf la cartographie du système, qu'il faut cocher expressément.</legend>
                {[...overview.domains].sort((a, b) => domainLabel(a).localeCompare(domainLabel(b), "fr")).map((d) => (
                  <label key={d} className="flex items-center gap-2 text-sm text-slate-700 min-h-[36px]">
                    <Checkbox checked={form.domains.includes(d)} onCheckedChange={() => toggle(d)} data-testid={`domain-${d}`} />
                    {domainLabel(d)}
                  </label>
                ))}
              </fieldset>
            )}
          </div>
        </form>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>Annuler</Button>
          <Button type="submit" form="create-token-form" disabled={saving} data-testid="btn-create-token">{saving ? "Création…" : "Créer le jeton"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SecretDialog({ created, onClose }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [created]);
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (e) {
      toast.error("Copie impossible : sélectionnez le texte et copiez-le à la main.");
    }
  };
  return (
    <Dialog open={!!created} onOpenChange={(next) => !next && onClose()}>
      <DialogContent data-testid="secret-dialog">
        <DialogHeader>
          <DialogTitle>Copiez votre jeton maintenant</DialogTitle>
          <DialogDescription>Il ne sera plus jamais affiché. En cas de perte, révoquez-le et créez-en un autre.</DialogDescription>
        </DialogHeader>
        {created && (
          <div className="space-y-3">
            <code className="block break-all rounded-md border border-slate-200 bg-slate-50 p-3 text-sm select-all" data-testid="secret-value">{created.secret}</code>
            <Button variant="outline" className="w-full min-h-[44px]" onClick={() => copy(created.secret)} data-testid="btn-copy-secret">
              {copied ? <Check size={16} className="mr-2 text-emerald-600" aria-hidden /> : <Copy size={16} className="mr-2" aria-hidden />}
              {copied ? "Jeton copié" : "Copier le jeton"}
            </Button>
          </div>
        )}
        <DialogFooter>
          <Button onClick={onClose} data-testid="btn-secret-done">J'ai copié le jeton</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Paramètres › Agents externes (Ordre 25-5). automation:manage_agents: one's own tokens; the administrator sees and revokes everybody's. An agent
// acts as the account that created the token, read-only, with the rights that account has at each call.
export default function SettingsAgents() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState(null);
  const [toRevoke, setToRevoke] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  const load = useCallback(async () => {
    setLoadFailed(false);
    try {
      const { data } = await api.get("/agents/overview");
      setOverview(data);
    } catch (e) {
      setLoadFailed(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async () => {
    setBusy(true);
    try {
      await api.post(toRevoke.kind === "connection" ? `/agents/connections/${toRevoke.id}/revoke` : `/agents/tokens/${toRevoke.id}/revoke`);
      toast.success(toRevoke.kind === "connection" ? "Connexion révoquée : l'application est coupée" : "Jeton révoqué : l'agent est coupé");
      setToRevoke(null);
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(connectionSnippet(window.location.origin));
      setCopiedSnippet(true);
      setTimeout(() => setCopiedSnippet(false), 2000);
    } catch (e) {
      toast.error("Copie impossible : sélectionnez le texte et copiez-le à la main.");
    }
  };

  if (loadFailed) return <LoadError onRetry={load} />;
  if (overview == null) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-jamora" /></div>;

  const activeMine = overview.tokens.filter((t) => t.mine && t.state === "active").length;
  const full = activeMine >= overview.max_active;
  return (
    <div className="space-y-4" data-testid="settings-agents">
      <SectionHeader title="Agents externes" description="Donnez à un agent (Claude ou un autre outil compatible MCP) un accès en lecture seule à Jamora, avec vos propres droits." />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
        <div className="space-y-4 min-w-0">
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden min-w-0" data-testid="connections-section">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100">
            <h4 className="font-semibold text-sm text-slate-900">{overview.can_see_all ? "Applications connectées (tous les comptes)" : "Applications connectées"}</h4>
            <p className="text-xs text-slate-500 mt-0.5">Claude, ChatGPT, Claude Code, Codex… connectés avec votre autorisation, sans copier de jeton.</p>
          </div>
          {(overview.connections || []).length === 0 ? (
            <div className="px-5 py-10 text-center space-y-2" data-testid="connections-empty">
              <Plug size={28} className="mx-auto text-slate-400" aria-hidden />
              <p className="font-medium text-slate-800">Aucune application connectée</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Ajoutez l'adresse <code className="text-xs bg-slate-100 rounded px-1">{mcpUrl(window.location.origin)}</code> dans l'application (voir à droite), puis autorisez la connexion ici.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="connections-list">
              {overview.connections.map((c) => <TokenRow key={c.id} token={{ ...c, kind: "connection" }} showOwner={overview.can_see_all} onRevoke={setToRevoke} />)}
            </ul>
          )}
        </section>
        <section className="bg-white border border-slate-200 rounded-xl overflow-hidden min-w-0" data-testid="tokens-section">
          <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
            <h4 className="font-semibold text-sm text-slate-900">{overview.can_see_all ? "Jetons personnels de tous les comptes" : "Vos jetons personnels"}</h4>
            <Button size="sm" onClick={() => setCreating(true)} disabled={full} className="min-h-[44px] sm:min-h-0" data-testid="btn-new-token">
              <Plus size={14} className="mr-1.5" aria-hidden /> Créer un jeton
            </Button>
          </div>
          {full && <p className="px-4 sm:px-5 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-100" data-testid="limit-note">Vous avez déjà {overview.max_active} jetons actifs : révoquez-en un pour en créer un autre.</p>}
          {overview.tokens.length === 0 ? (
            <div className="px-5 py-12 text-center space-y-2" data-testid="tokens-empty">
              <KeyRound size={28} className="mx-auto text-slate-400" aria-hidden />
              <p className="font-medium text-slate-800">Aucun jeton pour le moment</p>
              <p className="text-sm text-slate-500 max-w-md mx-auto">Créez un jeton pour connecter un agent à Jamora. Il pourra consulter vos données, jamais les modifier.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="tokens-list">
              {overview.tokens.map((t) => <TokenRow key={t.id} token={t} showOwner={overview.can_see_all} onRevoke={setToRevoke} />)}
            </ul>
          )}
        </section>
        </div>

        <aside className="space-y-4 min-w-0">
          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="guarantees-card">
            <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2"><ShieldCheck size={16} className="text-emerald-600" aria-hidden /> Ce que l'agent peut et ne peut pas faire</h4>
            <ul className="text-sm text-slate-600 space-y-1.5 list-disc pl-4">
              <li>Lire, avec vos droits d'aujourd'hui : retirez un droit à votre compte et l'agent le perd à son appel suivant.</li>
              <li>Jamais écrire, supprimer ni toucher aux droits et aux paramètres.</li>
              <li>Chaque appel est consigné (outil, résultat, durée), jamais son contenu.</li>
              <li>{overview.quota_per_hour} appels par heure au plus, par jeton.</li>
            </ul>
          </section>
          <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="connection-card">
            <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2"><Bot size={16} aria-hidden /> Connecter un agent</h4>
            <div className="space-y-1.5 text-xs text-slate-600" data-testid="connect-howto">
              <p><span className="font-medium text-slate-800">Claude.ai, ChatGPT :</span> ajoutez un connecteur personnalisé avec l'adresse <code className="bg-slate-100 rounded px-1 break-all">{mcpUrl(window.location.origin)}</code>, puis autorisez la connexion ici.</p>
              <p><span className="font-medium text-slate-800">Claude Code :</span></p>
              <pre className="bg-slate-50 border border-slate-200 rounded-md p-2 whitespace-pre-wrap break-all" data-testid="claude-code-command">{claudeCodeCommand(window.location.origin)}</pre>
              <p><span className="font-medium text-slate-800">Codex et autres :</span> ajoutez un serveur MCP HTTP avec la même adresse, ou utilisez un jeton personnel.</p>
            </div>
            <p className="text-xs text-slate-500 pt-1">Avec un jeton personnel, à coller dans les réglages de l'agent en remplaçant le jeton :</p>
            <pre className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 whitespace-pre-wrap break-all" data-testid="connection-snippet">{connectionSnippet(window.location.origin)}</pre>
            <Button variant="outline" size="sm" className="w-full min-h-[44px] sm:min-h-0" onClick={copySnippet} data-testid="btn-copy-snippet">
              {copiedSnippet ? <Check size={14} className="mr-1.5 text-emerald-600" aria-hidden /> : <Copy size={14} className="mr-1.5" aria-hidden />}
              {copiedSnippet ? "Copié" : "Copier cette configuration"}
            </Button>
          </section>
        </aside>
      </div>

      <CreateDialog open={creating} onOpenChange={setCreating} overview={overview} onCreated={(token) => { setCreated(token); load(); }} />
      <SecretDialog created={created} onClose={() => setCreated(null)} />
      <AlertDialog open={!!toRevoke} onOpenChange={(open) => !open && !busy && setToRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer « {toRevoke?.kind === "connection" ? toRevoke?.client_name : toRevoke?.label} » ?</AlertDialogTitle>
            <AlertDialogDescription>{toRevoke?.kind === "connection" ? "L'application sera coupée tout de suite. Pour la reconnecter, il faudra autoriser à nouveau la connexion depuis l'application." : "L'agent qui utilise ce jeton sera coupé tout de suite. Cette action est définitive : il faudra créer un nouveau jeton."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" onClick={(e) => { e.preventDefault(); revoke(); }} disabled={busy} data-testid="btn-revoke-confirm">Révoquer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
